/**
 * IntakeCard — compact card rendering of a single Oscar intake, used as the mobile fallback for the
 * Intakes table.
 *
 * Shows the deal headline (company + deal name), a colour-coded opportunity-type badge, the
 * assigned SA (human name, never a UUID), the dealdesk review provenance, how long ago
 * the intake arrived, and a green "NEW" badge for intakes received in the last 48 hours.
 * Leadership/admin see a "Review" action (opens the full-page wizard); everyone with access can
 * "Reject" (opens the reject modal).
 */
import { Building2, Clock, ShieldCheck, UserCheck } from 'lucide-react';

import type { OscarIntake } from '@/types/intake';
import {
  formatExactDateTime,
  intakeArrivedAt,
  intakeAssigneeName,
  intakeCompany,
  intakeDealName,
  isNewIntake,
  opportunityTypeBadgeClass,
  timeAgo,
} from '@/types/intake';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip } from '@/components/ui/tooltip';

export interface IntakeCardProps {
  intake: OscarIntake;
  /** Whether the current user (leadership/admin) may review & approve. */
  canApprove: boolean;
  onReview: (intake: OscarIntake) => void;
  onReject: (intake: OscarIntake) => void;
}

export function IntakeCard({
  intake,
  canApprove,
  onReview,
  onReject,
}: IntakeCardProps): JSX.Element {
  const companyName = intakeCompany(intake);
  const dealName = intakeDealName(intake);
  const assignee = intakeAssigneeName(intake);
  const arrivedAt = intakeArrivedAt(intake);

  return (
    <Card className="flex flex-col">
      <CardContent className="flex flex-1 flex-col gap-4">
        {/* Header: company + deal name */}
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate font-semibold text-text-primary" title={companyName}>
              {companyName}
            </h3>
            {isNewIntake(intake) && (
              <Badge variant="success" className="px-1.5 py-0 text-[10px] uppercase tracking-wide">
                New
              </Badge>
            )}
          </div>
          {dealName !== companyName && (
            <p className="truncate text-sm text-text-secondary" title={dealName}>
              {dealName}
            </p>
          )}
          <span className="font-mono text-xs text-text-secondary">{intake.jira_key}</span>
        </div>

        {/* Deal-intelligence badges */}
        <div className="flex flex-wrap items-center gap-1.5">
          {intake.opportunity_type && (
            <Badge variant="neutral" className={opportunityTypeBadgeClass(intake.opportunity_type)}>
              {intake.opportunity_type}
            </Badge>
          )}
        </div>

        {/* Firmographics */}
        <dl className="space-y-1.5 text-sm">
          {intake.company_domain && (
            <div className="flex items-center gap-2 text-text-secondary">
              <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">{intake.company_domain}</span>
            </div>
          )}
          {assignee && (
            <div className="flex items-center gap-2 text-text-secondary">
              <UserCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">Assigned SA: {assignee}</span>
            </div>
          )}
          {intake.dealdesk_reviewed_by && (
            <div className="flex items-center gap-2 text-text-secondary">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">Dealdesk: {intake.dealdesk_reviewed_by}</span>
            </div>
          )}
        </dl>

        {/* Footer: arrival time + actions */}
        <div className="mt-auto flex items-center justify-between gap-2 pt-2">
          {/* Relative time for scanning, exact date+time behind a hover AND focus tooltip — same
              treatment as the desktop table's Received cell (INT-05). A native `title` alone never
              fires on keyboard focus, so the span is a focus stop inside the Tooltip. */}
          {(() => {
            const exact = formatExactDateTime(arrivedAt);
            return (
              <Tooltip text={exact}>
                <span
                  // Only a focus stop when there is an exact date to reveal.
                  tabIndex={exact ? 0 : undefined}
                  title={exact ?? undefined}
                  className="flex items-center gap-1.5 rounded text-xs text-text-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                >
                  <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {arrivedAt ? `Arrived ${timeAgo(arrivedAt)}` : 'Arrival unknown'}
                </span>
              </Tooltip>
            );
          })()}
          <div className="flex items-center gap-2">
            {canApprove && (
              <Button size="sm" onClick={() => onReview(intake)}>
                Review
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onReject(intake)}>
              Reject
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default IntakeCard;
